import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useState } from "react";
import type { ConnectorDefinition } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { playClick } from "../../lib/sounds";
import { toSentence } from "../../lib/toSentence";
import { ConfirmModal } from "../ConfirmModal";
import { Icon } from "../Icon";
import { IntegrationButton } from "../IntegrationButton";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import {
  AuthorizedButton,
  AuthorizedInfo,
  AuthorizedLabel,
  CheckIcon,
} from "./authorizedChip";
import type { App } from "./Form";
import type { ConnectorAuthorize } from "./useConnectorAuthorize";

const Actions = styled("div")`
  display: flex;
  gap: 0px;
`;

const IconButton = styled("button")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: var(--surface-primary);
  cursor: pointer;
  color: var(--text-primary);

  &:hover {
    background: var(--alpha4);
  }

  &:active {
    opacity: 0.6;
  }
`;

type Props = {
  app: App;
  definition: ConnectorDefinition;
  disabled?: boolean;
  auth: ConnectorAuthorize;
};

export function OAuthFieldItem({ app, definition, disabled, auth }: Props) {
  const z = useZero();
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  const resetConnection = useCallback(() => {
    if (!app.oauthConnectionId) return;
    setConfirmResetOpen(true);
  }, [app.oauthConnectionId]);

  const oauthStatus = app.oauthConnection?.status;
  const oauthAuthorized =
    oauthStatus && ["connected", "obtained_code"].includes(oauthStatus);
  const oauthError = app.oauthConnection?.lastError;
  const authorizedAt = app.oauthConnection?.updatedAt;
  const grantType = definition.oauth?.grantType(app.configuration);
  const actionText =
    grantType === "client_credentials"
      ? `Connect with ${definition.name}`
      : `Authorize with ${definition.name}`;

  if (oauthAuthorized) {
    return (
      <div>
        <AuthorizedButton>
          <AuthorizedLabel>
            <img src={definition.logo} alt={definition.name} />
            <Text variant="text-regular-md">
              Authorized with {definition.name}
            </Text>
          </AuthorizedLabel>
          <Actions>
            <Tooltip content="Replace authorization">
              <IconButton
                type="button"
                onMouseDown={() => {
                  playClick();
                  auth.authorize();
                }}
                disabled={disabled}
              >
                <Icon name="refresh-ccw" size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip content="Remove authorization">
              <IconButton
                type="button"
                onMouseDown={() => {
                  playClick();
                  resetConnection();
                }}
                disabled={disabled}
              >
                <Icon name="circle-minus" size={16} />
              </IconButton>
            </Tooltip>
          </Actions>
        </AuthorizedButton>

        {authorizedAt && (
          <AuthorizedInfo>
            <CheckIcon />
            <Text variant="text-small" color="var(--text-secondary)">
              Authorized {formatRelativeTime(authorizedAt)}
            </Text>
          </AuthorizedInfo>
        )}
        <ConfirmModal
          open={confirmResetOpen}
          onClose={() => setConfirmResetOpen(false)}
          onConfirm={() => {
            if (app.oauthConnectionId) {
              z.mutate(
                sharedMutators.resetOAuthConnection({
                  oauthConnectionId: app.oauthConnectionId,
                })
              );
            }
            setConfirmResetOpen(false);
          }}
          title="Disconnect connector"
          description={`Disconnects this connector immediately. You’ll need to authorize it again to keep using it.`}
          confirmLabel="Disconnect"
          destructive
        />
      </div>
    );
  }

  return (
    <div>
      <IntegrationButton
        type="button"
        onClick={auth.authorize}
        disabled={disabled || auth.remaining.length > 0}
        isLoading={auth.isPending}
        logo={definition.logo}
        text={actionText}
      />
      {auth.remaining.length > 0 && (
        <AuthorizedInfo>
          <Icon name="info" size={12} color="var(--text-secondary)" />
          <Text variant="text-small" color="var(--text-secondary)">
            Fill out {toSentence(auth.remaining.map((t) => t.field))} first
          </Text>
        </AuthorizedInfo>
      )}
      {(auth.authError || (oauthStatus === "error" && oauthError)) && (
        <AuthorizedInfo>
          <Icon name="info" size={12} />
          <Text variant="text-small" color="var(--text-error)">
            {auth.authError ?? oauthError}
          </Text>
        </AuthorizedInfo>
      )}
    </div>
  );
}
